import { useEffect, useLayoutEffect } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { THEME } from 'constants/interfaces';

import { useAppDispatch, useAppSelector } from '../store/hooks/useApp';
import { authTokensSelector } from '../store/slices/auth/selectors';
import selectCurrentTheme from '../store/slices/theme/selectors';
import { setTheme, toggleTheme } from '../store/slices/theme/slice';
import { useUserMeQuery } from '../store/slices/user/apis/user';

import type IUseTheme from './interfaces/IUseTheme';

function useTheme(): IUseTheme {
  const dispatch = useAppDispatch();
  const theme = useAppSelector(selectCurrentTheme);

  const { accessToken } = useAppSelector(authTokensSelector);

  const queryParams = accessToken ? undefined : skipToken;
  const { data: me } = useUserMeQuery(queryParams);

  const { colorPalette } = me?.data?.company?.uiConfiguration || {};

  // Auth pages (and any logged-out view) always use light theme tokens.
  // Super Admin dark mode is persisted and must not leak onto login.
  const effectiveTheme = accessToken ? theme : THEME.light;

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', effectiveTheme);

    if (!accessToken) {
      ['primary', 'secondary', 'headings1', 'headings2', 'subtitle'].forEach((key) => {
        document.documentElement.style.removeProperty(`--${key}`);
      });
      return;
    }

    if (colorPalette) {
      const {
        primary, secondary, headings1, headings2, subtitle,
      } = colorPalette;
      document.documentElement.style.setProperty('--primary', primary); // TODO - Refactor this to use a loop
      document.documentElement.style.setProperty('--secondary', secondary);
      document.documentElement.style.setProperty('--headings1', headings1);
      document.documentElement.style.setProperty('--headings2', headings2);
      document.documentElement.style.setProperty('--subtitle', subtitle);
    }
  }, [accessToken, effectiveTheme, me, colorPalette]);

  useEffect(() => {
    if (!accessToken && theme !== THEME.light) {
      dispatch(setTheme(THEME.light));
    }
  }, [accessToken, dispatch, theme]);

  const resetToDefaultTheme = () => {
    if (colorPalette) {
      Object.keys(colorPalette).forEach((key) => {
        document.documentElement.style.removeProperty(`--${key}`);
      });
    }
  };

  const onToggleTheme = () => {
    dispatch(toggleTheme());
  };

  return {
    theme, resetToDefaultTheme, onToggleTheme,
  };
}

export default useTheme;
